/**
 * Service layer — Design domain
 *
 * Owns: Owner Criteria (tblc8Ckf8rR5aNKGP),
 *       Parcels (tblBcOe6dw3wSlave),
 *       Project Concepts (tblaAwLsCi6XGsdFY),
 *       Concept Units / Levels / Rooms (tblmkEv2v582TifCm / tblCjGs2HRCAaa9I1 / tbl7sWrKq7bmvctSx),
 *       Coord Items (tblOdNjmrVanO9quT),
 *       Specifications (tblrCqmsGbBCTldzZ)
 *
 * Note: DesignSection.jsx's inline tree + zoom pages still read/write the
 * Concept Unit/Level hierarchy through the outline engine's ctx.unitCache /
 * ctx.levelCache singletons (populated by outline/design.js's loadUnits/
 * loadLevels) rather than through this file's getConceptUnits/getConceptLevels
 * directly — that migration is still pending. Room CRUD (getLevelRooms/
 * createRoom/updateRoom/deleteRoom) and the cascade deletes are the first
 * pieces used directly by both DesignSection.jsx and the DAPC Design node.
 */

import { apiGet, apiGetAll, apiPost, apiPatch, apiDelete, apiDeleteBatch } from './_api.js'

const TBL_CRITERIA      = 'tblc8Ckf8rR5aNKGP'
const TBL_CONCEPT       = 'tblaAwLsCi6XGsdFY'
const TBL_CONCEPT_UNIT  = 'tblmkEv2v582TifCm'
const TBL_CONCEPT_LEVEL = 'tblCjGs2HRCAaa9I1'
const TBL_ROOMS     = 'tbl7sWrKq7bmvctSx'
const TBL_ROOM_TPL  = 'tblslHjXgMJb59po0'
const TBL_BUDGET_MODEL = 'tblEdUZrWv4mbudzU'
const TBL_SPECS     = 'tblrCqmsGbBCTldzZ'
const TBL_ORGS      = 'tblsPUYYYOTNkiXUt'

/** Fetch all Owner Criteria for a project. */
export function getCriteria(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGet(TBL_CRITERIA, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=10')
}

/* Parcels moved to services/parcels.js (CT-020). */

/** Fetch all Project Concepts for a project. */
export function getConcepts(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGet(TBL_CONCEPT, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=20')
}

// projectId → Promise<conceptId|null>. Cleared by setSelectedConcept.
const _selectedConceptCache = {}

/**
 * The project's Selected Concept id — or, when nothing is marked selected but the project has
 * exactly one concept, that one. null when it can't be decided (none, or several unselected).
 * Cached per project for the session; a failed read is not cached.
 */
export function getSelectedConceptId(projectId) {
  if (!projectId) return Promise.resolve(null)
  if (!_selectedConceptCache[projectId]) {
    _selectedConceptCache[projectId] = getConcepts(projectId).then(concepts => {
      const list = concepts || []
      const sel = list.find(c => c.fields && c.fields['Selected Concept'] === true)
      if (sel) return sel.id
      return list.length === 1 ? list[0].id : null
    }).catch(err => { delete _selectedConceptCache[projectId]; throw err })
  }
  return _selectedConceptCache[projectId]
}

/** Record a just-made selection so later reads don't need a refetch. */
export function noteSelectedConcept(projectId, conceptId) {
  if (projectId) _selectedConceptCache[projectId] = Promise.resolve(conceptId || null)
}

/** Update editable fields on a Project Concept record (e.g. the Development Metrics "Proposed" fields). */
export function updateConcept(conceptId, fields) {
  return apiPatch(TBL_CONCEPT, conceptId, fields)
}

/**
 * Concept names are UNIQUE per project (Matt, 2026-10-07) — compared ignoring case and
 * surrounding/repeated whitespace, so "Option A" and "option  a" count as the same name.
 * Every path that sets a name (Add, Copy, Edit name) checks here before writing, and
 * api/concept-copy.js repeats the check server-side.
 */
export function conceptNameKey(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase()
}

/** The concept in `concepts` already using `name` (by conceptNameKey), ignoring `excludeId`; else null. */
export function findConceptByName(concepts, name, excludeId) {
  const key = conceptNameKey(name)
  if (!key) return null
  return (concepts || []).find(c => c.id !== excludeId && conceptNameKey(c.fields && c.fields['Parti Pris Title']) === key) || null
}

/** Throws when another concept on the project already has this name (reads the live list). */
export async function assertConceptNameFree(projectId, name, excludeId) {
  const concepts = await getConcepts(projectId)
  if (findConceptByName(concepts, name, excludeId)) {
    throw new Error('A concept named "' + String(name).trim() + '" already exists on this project. Choose a different name.')
  }
}

/** Create a new Project Concept. fields e.g. { 'Parti Pris Title': name }. Refuses a name already in use. */
export async function createConcept(projectId, fields) {
  if (fields && fields['Parti Pris Title']) await assertConceptNameFree(projectId, fields['Parti Pris Title'])
  return apiPost(TBL_CONCEPT, { 'Project': [projectId], ...fields })
}

/** Rename a concept. Refuses a name another concept on the project already uses. */
export async function renameConcept(projectId, conceptId, name) {
  await assertConceptNameFree(projectId, name, conceptId)
  return apiPatch(TBL_CONCEPT, conceptId, { 'Parti Pris Title': String(name).trim() })
}

/**
 * Copy a concept into a new, independent concept on the same project — the concept record, its
 * Units → Levels → Rooms, the AlderCAD model (rooms/levels re-linked to the new rows) and the
 * Building Information Model plans. One server call (api/concept-copy.js) that rolls back on any
 * failure. Resolves { concept: { id, fields }, counts }.
 */
export async function copyConcept(conceptId, projectId, name) {
  const token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken() : null
  const res = await fetch('/api/concept-copy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ conceptId, projectId, name }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Copy failed (' + res.status + ')'))
  return data
}

/**
 * Mark a concept as the project's Selected Concept — only one may be selected at a time, so this
 * clears the flag on every other concept in the project first. Selection drives presentations,
 * specs, and schedules (see ConceptRow / DapcConceptNode / spec + schedule editors).
 */
export async function setSelectedConcept(projectId, conceptId) {
  delete _selectedConceptCache[projectId]
  const concepts = await getConcepts(projectId)
  const others = concepts.filter(c => c.id !== conceptId && c.fields['Selected Concept'] === true)
  await Promise.all(others.map(c => apiPatch(TBL_CONCEPT, c.id, { 'Selected Concept': false })))
  const res = await apiPatch(TBL_CONCEPT, conceptId, { 'Selected Concept': true })
  // Drawing-sheet PDF Inserts follow the active concept. Dynamic import: pdfInserts.js
  // imports this file. Best-effort — never fails the selection itself.
  import('./pdfInserts.js')
    .then(m => m.relinkProjectPdfInserts(projectId, conceptId))
    .catch(err => console.warn('[setSelectedConcept] PDF insert relink failed', err))
  return res
}

/** Fetch Concept Units for a concept, for use in the DAPC Design section. */
export function getConceptUnits(conceptId) {
  const formula = 'FIND("' + conceptId + '",ARRAYJOIN({Record UID (from Project Concept)},","))'
  return apiGet(TBL_CONCEPT_UNIT, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=20')
}

/** Fetch Concept Levels for a unit, sorted by Stacking Order. */
export function getConceptLevels(unitId) {
  const formula = 'FIND("' + unitId + '",ARRAYJOIN({Record ID (from Concept Unit)},","))'
  return apiGet(
    TBL_CONCEPT_LEVEL,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Stacking+Order&sort%5B0%5D%5Bdirection%5D=asc'
    + '&maxRecords=20'
  )
}

/** Update editable fields on a Concept Level record. Returns the updated record. */
export function updateConceptLevel(levelId, fields) {
  return apiPatch(TBL_CONCEPT_LEVEL, levelId, fields)
}

/**
 * AREA PLAN COMPLETION (docs/bim-plan/28 round 6): the Modeler's area breakout use → the Concept
 * Level's area field family. ADU living area has no field on the Concept Level, so it is not
 * sent (the Send window says so).
 */
const AREA_FIELD_FAMILY = {
  living: 'Living Area',
  garage: 'Garage Area',
  deck: 'Elevated Decks Area',
  covered: 'Outdoor Covered Area',
  storage: 'Mechanical Storage Area',
}
const AREA_STATUS_PREFIX = { new: 'New', existing: 'Existing', remodel: 'Remodel' }

/**
 * Write a Modeler level's area totals into a Concept Level — every New / Existing / Remodel field
 * of the five families, zero where the breakout has none, so the Concept reads exactly what the
 * Area Plan measured. `totals` = kernel areaTotalsForLevel(model, levelId). Returns the fields
 * written and the square feet left out (ADU).
 */
export async function pushConceptLevelAreas(conceptLevelId, totals) {
  const fields = {}
  for (const [use, family] of Object.entries(AREA_FIELD_FAMILY)) {
    for (const [status, prefix] of Object.entries(AREA_STATUS_PREFIX)) {
      fields[`${prefix} ${family}`] = Math.round((totals?.[use]?.[status] || 0) * 100) / 100
    }
  }
  const skippedSf = Object.values(totals?.adu || {}).reduce((a, v) => a + (v || 0), 0)
  await apiPatch(TBL_CONCEPT_LEVEL, conceptLevelId, fields)
  return { fields, skippedSf }
}

/** Create a new Concept Unit under a concept. unitType must be one of the Unit Type choices. */
export function createConceptUnit(conceptId, unitType) {
  return apiPost(TBL_CONCEPT_UNIT, { 'Project Concept': [conceptId], 'Unit Type': unitType })
}

/** Create a new Concept Level under a unit. fields e.g. { Name, 'Stacking Order' }. */
export function createConceptLevel(unitId, fields) {
  return apiPost(TBL_CONCEPT_LEVEL, { 'Concept Unit': [unitId], ...fields })
}

/** Update editable fields on a Concept Unit record (e.g. Unit Type — Name is a formula off it). */
export function updateConceptUnit(unitId, fields) {
  return apiPatch(TBL_CONCEPT_UNIT, unitId, fields)
}

/**
 * Delete a Concept Level and every Room under it. Fetches rooms first so the
 * caller doesn't have to — callers that already have the room list (e.g. for
 * a confirmation count) should still call this; the extra fetch is cheap.
 */
export async function deleteConceptLevelCascade(levelId) {
  const rooms = await getLevelRooms(levelId)
  if (rooms.length) await apiDeleteBatch(TBL_ROOMS, rooms.map(r => r.id))
  await apiDelete(TBL_CONCEPT_LEVEL, levelId)
}

/** Delete a Concept Unit, every Level under it, and every Room under those levels. */
export async function deleteConceptUnitCascade(unitId) {
  const levels = await getConceptLevels(unitId)
  for (const level of levels) {
    const rooms = await getLevelRooms(level.id)
    if (rooms.length) await apiDeleteBatch(TBL_ROOMS, rooms.map(r => r.id))
  }
  if (levels.length) await apiDeleteBatch(TBL_CONCEPT_LEVEL, levels.map(l => l.id))
  await apiDelete(TBL_CONCEPT_UNIT, unitId)
}

// Rooms and Spaces "Level" singleSelect choices — kept in sync with the Concept Level's
// Name on create, mirroring the legacy openAddRoomModal behavior in outline.js.
const ROOM_LEVEL_SINGLESELECT_OPTIONS = ['Fifth', 'Fourth', 'Third', 'Second', 'First', 'Site']

/** Fetch Rooms and Spaces linked to a Concept Level. */
export function getLevelRooms(levelId) {
  const formula = 'FIND("' + levelId + '",ARRAYJOIN({Record ID (from Concept Level)},","))'
  return apiGet(TBL_ROOMS, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=100')
}

/** All Room Templates — a small, global reference table (not project-scoped).
 *  structuralBaseFactorPct is the admin-curated '% adjustment relative to a standard room of the
 *  same classification' (e.g. Kitchen higher, Guest Bedroom lower) — the Airtable percent field
 *  already returns as a decimal fraction (0.30 for +30%), which is exactly the term
 *  deriveCostFactor below adds to 1. See docs/MIGRATION_DERIVATIONS.md. */
export async function getRoomTemplates() {
  const recs = await apiGet(TBL_ROOM_TPL, 'maxRecords=200')
  return recs
    .map(r => ({
      id: r.id,
      name: r.fields['Name'] || '',
      classification: r.fields['Classification'] || '',
      structuralBaseFactorPct: Number(r.fields['Structural Base Factor']) || 0,
      structuralBaseFactorNotes: r.fields['Structural Base Factor Notes'] || '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Fixed, firm-wide bumps (decimal fraction) that a room's Structural Characteristics add to its
 *  Cost Factor — physical framing/structure facts, not per-project business tuning, so they live
 *  as a code constant rather than a Control Budget Model override. Extend this map (and the
 *  matching Airtable 'Structural Characteristics' multi-select option) to add more later. */
export const STRUCTURAL_CHARACTERISTIC_FACTORS = {
  'Double-Height': 0.20,
}

/** Cost Factor is now an Airtable FORMULA field on Rooms and Spaces (Round 6) — Airtable itself
 *  computes `1 + {Structural Base Factor (from Room Template)} + IF(FIND("Double-Height",
 *  ARRAYJOIN({Structural Characteristics},",")), 0.2, 0)` live, feeding `Budget Room Cost
 *  Low/High` with no app write and no staleness risk (a Room Template's baseline changing
 *  retroactively recomputes every room using it, instantly). The app never writes Cost Factor.
 *
 *  This function mirrors that same formula purely so ProgramModelModal.jsx's Properties palette
 *  can show the correct number for an UNSAVED draft (a Room Template/Structural Characteristics
 *  change the user hasn't hit Save on yet) — Airtable obviously can't compute a value for data
 *  it hasn't received. Once saved, the room's own `Cost Factor` field (read via room.fields) is
 *  authoritative; this is a preview only. Deliberately excludes finish quality — the Control
 *  Budget Model's low-high $/SF range already carries that variance. Keep this logic in sync
 *  with the Airtable formula if either one changes. */
export function deriveCostFactor(templates, roomTemplateId, characteristics) {
  const template = (templates || []).find(t => t.id === roomTemplateId)
  const base = Number(template?.structuralBaseFactorPct) || 0
  const bump = (characteristics || []).reduce((sum, c) => sum + (STRUCTURAL_CHARACTERISTIC_FACTORS[c] || 0), 0)
  return Math.round((1 + base + bump) * 100) / 100
}

/** All Control Budget Models — a small, admin-curated $/SF cost-template catalog assignable to
 *  a Project Concept via its own 'Control Budget Model' link field (see ControlBudgetSection.jsx).
 *  NOT the separate 'Control Budgets' table (per-GC bid estimates) — similarly named, unrelated. */
export async function getControlBudgetModels() {
  const recs = await apiGet(TBL_BUDGET_MODEL, 'maxRecords=200')
  return recs
    .map(r => ({ id: r.id, name: r.fields['Name'] || '' }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Full Room Template records (admin editor, RoomTemplatesEditor.jsx) — every raw field, not the
 *  minimal { id, name, classification, structuralBaseFactorPct } shape getRoomTemplates() returns
 *  for pickers elsewhere in the app. */
export async function getRoomTemplatesFull() {
  const recs = await apiGet(TBL_ROOM_TPL, 'maxRecords=200')
  return recs.slice().sort((a, b) => (a.fields['Name'] || '').localeCompare(b.fields['Name'] || ''))
}

export async function updateRoomTemplate(id, fields) {
  return assertOk(await apiPatch(TBL_ROOM_TPL, id, fields), 'Update room template')
}

export async function createRoomTemplate(fields) {
  return assertOk(await apiPost(TBL_ROOM_TPL, fields), 'Create room template')
}

export async function deleteRoomTemplate(id) {
  return assertOk(await apiDelete(TBL_ROOM_TPL, id), 'Delete room template')
}

/** Full Control Budget Model records (admin editor, ControlBudgetModelsEditor.jsx). */
export async function getControlBudgetModelsFull() {
  const recs = await apiGet(TBL_BUDGET_MODEL, 'maxRecords=200')
  return recs.slice().sort((a, b) => (a.fields['Name'] || '').localeCompare(b.fields['Name'] || ''))
}

export async function updateControlBudgetModel(id, fields) {
  return assertOk(await apiPatch(TBL_BUDGET_MODEL, id, fields), 'Update control budget model')
}

export async function createControlBudgetModel(fields) {
  return assertOk(await apiPost(TBL_BUDGET_MODEL, fields), 'Create control budget model')
}

export async function deleteControlBudgetModel(id) {
  return assertOk(await apiDelete(TBL_BUDGET_MODEL, id), 'Delete control budget model')
}

// Airtable rejects an empty string '' for a singleSelect field on create/update (it wants either
// a real choice or the field omitted entirely) — strip blank strings for THOSE fields only, so an
// unset singleSelect is left alone instead of erroring the whole write. This was the root cause of
// "added a room but it never appeared": the old payload always sent 'Scope Type': '' when nothing
// was picked, Airtable rejected the write, and the failure was never surfaced (see assertOk).
// Scoping the strip to singleSelect fields matters: the original blanket version (any blank string,
// any field) also silently dropped 'Room Number' from the payload whenever it was cleared to blank —
// Room Number is a plain text field and the one Room field the edit modals deliberately allow to be
// blank, so the clear never reached Airtable at all and the old value stuck around looking unsaved.
const ROOM_SINGLE_SELECT_FIELDS = new Set(['Scope Type'])
function cleanRoomFields(fields) {
  const out = {}
  for (const key of Object.keys(fields || {})) {
    const v = fields[key]
    if (ROOM_SINGLE_SELECT_FIELDS.has(key) && typeof v === 'string' && v.trim() === '') continue
    out[key] = v
  }
  return out
}

// apiPost/apiPatch never throw on an Airtable error response (see services/_api.js) — they
// resolve with whatever Airtable sent back, success or {error}. Room writes now check explicitly
// so a rejected write surfaces as a real failure instead of the modal closing as if it worked.
function assertOk(res, action) {
  if (res && res.error) {
    throw new Error((res.error && (res.error.message || res.error.type)) || action + ' failed')
  }
  return res
}

/**
 * Create a Room under a Concept Level. fields: { 'Room Number'?, 'Custom Name', 'Scope Type',
 * 'Room Template': [id] } — Name/Scope/Template are required by the Add/Edit Room modal; Room
 * Number stays optional. levelName syncs the legacy "Level" singleSelect when it matches a known option.
 */
export async function createRoom(levelId, conceptId, levelName, fields) {
  // Cost Factor is an Airtable FORMULA field (Round 6) — never send it here; Airtable computes
  // it itself the instant Room Template is set (and the API would reject a write to it anyway).
  const payload = { ...cleanRoomFields(fields), 'Concept Level': [levelId] }
  if (conceptId) payload['Project Concept'] = [conceptId]
  const match = ROOM_LEVEL_SINGLESELECT_OPTIONS.find(o => o.toLowerCase() === String(levelName || '').toLowerCase())
  if (match) payload['Level'] = match
  return assertOk(await apiPost(TBL_ROOMS, payload), 'Create room')
}

/** Update editable fields on a Room record (Room Number, Custom Name, Scope Type, Room Template). */
export async function updateRoom(roomId, fields) {
  return assertOk(await apiPatch(TBL_ROOMS, roomId, cleanRoomFields(fields)), 'Update room')
}

/** Delete a single Room record. */
export function deleteRoom(roomId) {
  return apiDelete(TBL_ROOMS, roomId)
}

/**
 * Move a Room to a different Concept Level (AlderCAD Room Schedule). Keeps the legacy
 * "Level" singleSelect in step the same way createRoom sets it; clears it when the new
 * level's name is not one of its options so it never points at the old level.
 */
export async function moveConceptRoomToLevel(roomId, levelId, levelName) {
  const match = ROOM_LEVEL_SINGLESELECT_OPTIONS.find(o => o.toLowerCase() === String(levelName || '').toLowerCase())
  return assertOk(await apiPatch(TBL_ROOMS, roomId, { 'Concept Level': [levelId], 'Level': match || null }), 'Move room')
}

/** Delete a Room record, throwing when Airtable refuses (deleteRoom resolves either way). */
export async function deleteConceptRoom(roomId) {
  return assertOk(await apiDelete(TBL_ROOMS, roomId), 'Delete room')
}

/**
 * GL3 (AlderCAD rooms): the whole Concept's room hierarchy in ONE grouped shape —
 * Units → Levels → Rooms, plus rooms with no Concept Level under `unassigned`.
 * Rooms come from a single concept-wide query (the outline/design.js pattern — one
 * Airtable call instead of one per level) grouped client-side by the Concept Level
 * link. Room `name` follows the outline display convention; `areaManual` is the
 * current Airtable `Area Manual` value (the field AlderCAD's explicit "Sync areas
 * to Concept" action writes through updateRoom — see docs/MIGRATION_DERIVATIONS.md
 * "Rooms · Area Manual").
 */
export async function getConceptRoomsGrouped(conceptId) {
  const roomsFormula = 'FIND("' + conceptId + '",ARRAYJOIN({Record UID (from Project Concept)},","))'
  const [units, allRooms] = await Promise.all([
    getConceptUnits(conceptId),
    apiGet(TBL_ROOMS, 'filterByFormula=' + encodeURIComponent(roomsFormula) + '&maxRecords=200'),
  ])
  const shapeRoom = r => ({
    id: r.id,
    name: r.fields['Room'] || r.fields['Room Number and Name'] || 'Room',
    roomNumber: r.fields['Room Number'] || '',
    areaManual: typeof r.fields['Area Manual'] === 'number' ? r.fields['Area Manual'] : null,
    // the editable settings the AlderCAD Room Schedule shows and writes (raw inputs only)
    customName: r.fields['Custom Name'] || '',
    scopeType: r.fields['Scope Type'] || '',
    templateId: Array.isArray(r.fields['Room Template']) ? (r.fields['Room Template'][0] || '') : '',
    length: typeof r.fields['Length'] === 'number' ? r.fields['Length'] : null,
    width: typeof r.fields['Width'] === 'number' ? r.fields['Width'] : null,
  })
  const byLevel = {}
  const unassigned = []
  for (const r of (allRooms || [])) {
    const levelId = Array.isArray(r.fields['Concept Level']) ? r.fields['Concept Level'][0] : null
    if (levelId) (byLevel[levelId] = byLevel[levelId] || []).push(shapeRoom(r))
    else unassigned.push(shapeRoom(r))
  }
  const sortRooms = list => list.sort((a, b) =>
    String(a.roomNumber || a.name).localeCompare(String(b.roomNumber || b.name), undefined, { numeric: true }))
  const out = { units: [], unassigned: sortRooms(unassigned) }
  for (const u of (units || [])) {
    const levels = await getConceptLevels(u.id)
    out.units.push({
      id: u.id,
      name: u.fields['Unit Type'] || u.fields['Name'] || 'Unit',
      levels: (levels || []).map(l => ({
        id: l.id,
        name: l.fields['Name'] || 'Level',
        rooms: sortRooms(byLevel[l.id] || []),
      })),
    })
  }
  return out
}

/**
 * GL3: the explicit "Sync areas to Concept" write — one room's derived net SF into
 * Airtable `Area Manual` (rounded whole SF, the Program Model tool's convention).
 * NEVER called automatically: the Program Model tool also writes this field (and
 * silently reconciles rooms that have a program polygon), so AlderCAD only writes
 * on the user's explicit action. Throws on an Airtable error (assertOk via updateRoom).
 */
export function syncRoomAreaManual(roomId, areaSqFt) {
  return updateRoom(roomId, { 'Area Manual': areaSqFt == null ? null : Math.round(areaSqFt) })
}

/**
 * Fetch all Organizations, sorted by name.
 * Returns [{id, name}] — used to populate the External Organization picker.
 */
export function getOrganizations() {
  // No fields[] filter — Organization Name may be a formula field which fields[] silently drops.
  // Sort client-side for the same reason (server-side sort on a formula times out).
  return apiGetAll(TBL_ORGS, '').then(records => {
    const mapped = records.map(r => ({ id: r.id, name: r.fields['Organization Name'] || r.fields['Name'] || r.id }))
    mapped.sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
    return mapped
  })
}

/**
 * Fetch all Specifications for a project.
 * Uses the Project Concepts lookup path for the project filter.
 */
export function getSpecifications(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project) (from Project Concepts)},","))'
  return apiGet(TBL_SPECS, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=20')
}
